import type React from "react";
import {Link as RouterLink} from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import {colors} from "../../theme/tokens";

export type Breadcrumb = {
    label: string;
    to?: string;
    /** Accessible name when the visible label alone is ambiguous; must contain the visible label. */
    ariaLabel?: string;
    /** Runs before navigation; call `preventDefault` to stay, e.g. to confirm discarding edits. */
    onClick?: (event: React.MouseEvent) => void;
};

type PageHeaderProps = {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    breadcrumbs?: Breadcrumb[];
    /** Inline chips or status next to the title (Edit tenant shows the tenant ID and login state). */
    titleAdornment?: React.ReactNode;
};

/** Page title row of every authenticated view; edit and create pages add a breadcrumb and a larger title. */
export function PageHeader({title, subtitle, actions, breadcrumbs, titleAdornment}: PageHeaderProps) {
    const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
    return (
            <Box sx={{display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap"}}>
                <Box sx={{flex: 1, minWidth: 240}}>
                    {hasBreadcrumbs && (
                            <Box component="nav" aria-label="Breadcrumb" sx={{fontSize: "12px", color: colors.muted, mb: 0.5}}>
                                {breadcrumbs.map((crumb, index) => (
                                        <Box component="span" key={`${crumb.label}-${index}`}>
                                            {index > 0 && <Box component="span" sx={{mx: 0.75, color: colors.faint}}>/</Box>}
                                            {crumb.to ? (
                                                    <Link component={RouterLink} to={crumb.to} underline="hover" aria-label={crumb.ariaLabel} onClick={crumb.onClick}>
                                                        {crumb.label}
                                                    </Link>
                                            ) : (
                                                    <span>{crumb.label}</span>
                                            )}
                                        </Box>
                                ))}
                            </Box>
                    )}
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap"}}>
                        <Typography variant={hasBreadcrumbs ? "h5" : "h4"} component="h1">
                            {title}
                        </Typography>
                        {titleAdornment}
                    </Box>
                    {subtitle && (
                            <Typography variant="subtitle2" component="div" sx={{mt: 0.25}}>
                                {subtitle}
                            </Typography>
                    )}
                </Box>
                {actions && <Box sx={{display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap"}}>{actions}</Box>}
            </Box>
    );
}
